import { z } from "zod";
import type { EntityField, FormTypeEnum } from "@/services/entity.service";

const DEFAULT_LOCALE = "de-DE";
const DEFAULT_CURRENCY = "EUR";
const ISO_4217 = /^[A-Z]{3}$/;

export interface FormatNumberOptions {
  fieldType?: string;
  formType?: FormTypeEnum | null;
  decimals?: number | null;
  grouping?: boolean | null;
  currency?: string | null;
  locale?: string | null;
  durationStyle?: FieldFormat["durationStyle"] | null;
}

// The backend stores a field's `format` as given, so this is the only definition of its keys.
// An invalid key is dropped rather than failing the whole format.
const fieldFormatSchema = z
  .object({
    decimals: z.number().int().min(0).max(20).optional().catch(undefined),
    grouping: z.boolean().optional().catch(undefined),
    currency: z.string().regex(ISO_4217).optional().catch(undefined),
    locale: z.string().max(10).optional().catch(undefined),
    durationStyle: z.enum(["DECIMAL", "CLOCK"]).optional().catch(undefined),
  })
  .catch({});

export type FieldFormat = z.infer<typeof fieldFormatSchema>;
export type DurationStyle = NonNullable<FieldFormat["durationStyle"]>;

export const parseFieldFormat = (raw: unknown): FieldFormat => fieldFormatSchema.parse(raw ?? {});

export const toFieldFormat = (format: {
  [K in keyof FieldFormat]?: FieldFormat[K] | null;
}): FieldFormat | null => {
  const set = Object.entries(format).filter(([, value]) => value != null);
  return set.length ? Object.fromEntries(set) : null;
};

export const fieldFormatOptions = (
  field: Pick<EntityField, "data_type" | "form_type" | "format">,
): FormatNumberOptions => {
  const format = parseFieldFormat(field.format);
  return {
    fieldType: String(field.data_type),
    formType: field.form_type,
    decimals: format.decimals,
    grouping: format.grouping,
    currency: format.currency,
    locale: format.locale,
    durationStyle: format.durationStyle,
  };
};

// Intl.NumberFormat construction is expensive (locale lookup + format compilation);
// the table renders thousands of cells, so cache instances by their config.
const formatterCache = new Map<string, Intl.NumberFormat>();
const getFormatter = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let instance = formatterCache.get(key);
  if (!instance) {
    instance = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, instance);
  }
  return instance;
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
};

export function formatFieldValue(value: unknown, opts: FormatNumberOptions): string {
  if (value === null || value === undefined || value === "") return "";

  const fieldType = opts.fieldType?.toUpperCase();
  const formType = opts.formType ?? null;
  const locale = opts.locale || DEFAULT_LOCALE;
  const grouping = opts.grouping ?? true;
  const hasExplicitDecimals = opts.decimals != null;
  const minFraction = hasExplicitDecimals ? opts.decimals! : 0;
  const maxFraction = hasExplicitDecimals ? opts.decimals! : 20;

  if (formType === "CURRENCY") {
    const numericValue = toNumber(value);
    if (numericValue === null) return "";
    const rawCurrency = (opts.currency || DEFAULT_CURRENCY).toUpperCase();
    const currency = ISO_4217.test(rawCurrency) ? rawCurrency : DEFAULT_CURRENCY;
    try {
      return getFormatter(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: minFraction,
        maximumFractionDigits: maxFraction,
        useGrouping: grouping,
      }).format(numericValue);
    } catch {
      return getFormatter(locale, {
        minimumFractionDigits: minFraction,
        maximumFractionDigits: maxFraction,
        useGrouping: grouping,
      }).format(numericValue);
    }
  }

  if (formType === "PERCENT") {
    const numericValue = toNumber(value);
    if (numericValue === null) return "";
    return getFormatter(locale, {
      style: "percent",
      minimumFractionDigits: minFraction,
      maximumFractionDigits: maxFraction,
      useGrouping: grouping,
    }).format(numericValue);
  }

  // Duration values are hours; a value in another unit is converted in a formula.
  if (formType === "DURATION") {
    const hours = toNumber(value);
    if (hours === null) return "";
    if (opts.durationStyle === "CLOCK") {
      const totalMinutes = Math.round(Math.abs(hours) * 60);
      const wholeHours = Math.floor(totalMinutes / 60);
      const minutes = String(totalMinutes % 60).padStart(2, "0");
      const sign = hours < 0 && totalMinutes > 0 ? "-" : "";
      return `${sign}${getFormatter(locale, { useGrouping: grouping }).format(wholeHours)}:${minutes} h`;
    }
    return `${getFormatter(locale, {
      useGrouping: grouping,
      minimumFractionDigits: hasExplicitDecimals ? opts.decimals! : 0,
      maximumFractionDigits: hasExplicitDecimals ? opts.decimals! : 2,
    }).format(hours)} h`;
  }

  if (fieldType === "INT" || fieldType === "FLOAT") {
    const numericValue = toNumber(value);
    if (numericValue === null) return "";
    return getFormatter(locale, {
      useGrouping: grouping,
      minimumFractionDigits: minFraction,
      maximumFractionDigits: hasExplicitDecimals ? opts.decimals! : fieldType === "FLOAT" ? 20 : 0,
    }).format(numericValue);
  }

  return String(value);
}

export function getCurrencySymbol(
  currency: string | null | undefined,
  locale?: string | null,
): string {
  const code = (currency || DEFAULT_CURRENCY).toUpperCase();
  if (!ISO_4217.test(code)) return code;
  try {
    const parts = getFormatter(locale || DEFAULT_LOCALE, {
      style: "currency",
      currency: code,
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? code;
  } catch {
    return code;
  }
}

const trimPrecision = (n: number): string => parseFloat(n.toPrecision(12)).toString();

export const percentFractionToDisplay = (stored: string): string => {
  if (stored === "" || stored === "-") return stored;
  const n = parseFloat(stored);
  return Number.isFinite(n) ? trimPrecision(n * 100) : stored;
};

export const percentDisplayToFraction = (display: string): string => {
  if (display === "" || display === "-") return display;
  const n = parseFloat(display);
  return Number.isFinite(n) ? trimPrecision(n / 100) : display;
};
